import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlayCircle, AlertTriangle, FileText, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { apiClient } from '../../lib/api/config.js';

/**
 * Story synthesis queue panel (operator rulings 2026-10-06, M1).
 *
 * Shows exactly what the writer would get BEFORE queuing: the masthead
 * (thread owner by default, operator override), the released member
 * articles it will cite, and the deterministic aggregate. Queuing creates
 * the job first and only then marks the thread queued, so a refusal leaves
 * the thread untouched. The piece lands as a DRAFT for review.
 */

interface PreviewMember {
  n: number;
  article_id: string;
  name: string | null;
  exchange: string | null;
  code: string | null;
  title: string | null;
  lodged_at: string | null;
  activity_sub_type: string | null;
  price_sensitive: boolean;
}

interface Preview {
  target_name: string;
  default_target: string | null;
  content_product_id: string;
  products: { content_product_id: string; target_name: string; product_name: string }[];
  members: PreviewMember[];
  eligible_members: number;
  aggregate: { eligible_members: number; issuers: number; by_activity: Record<string, number>;
               date_from: string | null; date_to: string | null };
  existing_piece: { article_id: string; publish_status: string } | null;
}

const MASTHEAD_LABEL: Record<string, string> = {
  orecast: 'Orecast', marketfloor: 'The Market Floor', bellwire: 'Bell Wire',
};

function errorText(e: any): string {
  const d = e?.response?.data?.detail;
  if (d && typeof d === 'object' && d.message) return d.message;
  if (typeof d === 'string') return d;
  return e?.message || 'Request failed';
}

export function StorySynthesisPanel({ threadId, threadType, status, metadata: rawMetadata }: {
  threadId: string;
  threadType: string;
  status: string;
  // The thread API returns jsonb as a string (no codec on that pool).
  metadata: Record<string, any> | string | null | undefined;
}) {
  let metadata: Record<string, any> | null = null;
  if (typeof rawMetadata === 'string') {
    try { metadata = JSON.parse(rawMetadata); } catch { metadata = null; }
  } else {
    metadata = rawMetadata ?? null;
  }
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const eligibleType = ['sector', 'activity', 'entity'].includes(threadType);
  const canQueue = ['detected', 'failed', 'generated'].includes(status);

  const preview = useQuery<Preview>({
    queryKey: ['synthesis-preview', threadId, productId ?? 'default'],
    queryFn: async () => (await apiClient.get(`/admin/threads/${threadId}/synthesis-preview`, {
      params: productId ? { content_product_id: productId } : {},
    })).data,
    enabled: eligibleType && canQueue,
    retry: false,
  });

  const queue = useMutation({
    mutationFn: async () => (await apiClient.post(`/admin/threads/${threadId}/queue-synthesis`, {
      content_product_id: preview.data?.content_product_id,
      member_article_ids: preview.data?.members.map((m) => m.article_id),
      rewrite: status === 'generated',
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thread-detail', threadId] });
      queryClient.invalidateQueries({ queryKey: ['synthesis-preview', threadId] });
    },
  });

  if (!eligibleType) {
    return (
      <p className="text-sm text-grey-500">
        {threadType.replace('_', ' ')} threads are not synthesised (sector, activity and entity threads only).
      </p>
    );
  }

  const articleId = metadata?.synthesis_article_id as string | undefined;

  return (
    <div className="space-y-4">
      {status === 'failed' && metadata?.synthesis_error && (
        <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Last synthesis failed: {String(metadata.synthesis_error)}</span>
        </div>
      )}
      {(status === 'queued' || status === 'generating') && (
        <p className="text-sm text-grey-600">
          Synthesis {status} for {MASTHEAD_LABEL[metadata?.synthesis_target] || metadata?.synthesis_target || 'its masthead'}.
        </p>
      )}
      {articleId && (
        <Link to={`/articles/${articleId}`}
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium">
          <FileText className="h-4 w-4" /> Open the synthesis draft
        </Link>
      )}

      {canQueue && (
        <>
          {preview.isLoading && <p className="text-sm text-grey-500">Selecting members…</p>}
          {preview.isError && (
            <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
              {errorText(preview.error)}
            </div>
          )}
          {preview.data && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm text-grey-600" htmlFor="synth-masthead">Masthead</label>
                <select
                  id="synth-masthead"
                  value={preview.data.content_product_id}
                  onChange={(e) => setProductId(e.target.value)}
                  className="border border-grey-300 rounded-lg px-3 py-2 text-sm"
                >
                  {preview.data.products.map((p) => (
                    <option key={p.content_product_id} value={p.content_product_id}>
                      {MASTHEAD_LABEL[p.target_name] || p.target_name}
                      {p.target_name === preview.data!.default_target ? ' (owner)' : ''}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-grey-500">
                  {preview.data.members.length} of {preview.data.eligible_members} released articles ·{' '}
                  {preview.data.aggregate.issuers} issuer{preview.data.aggregate.issuers === 1 ? '' : 's'}
                </span>
              </div>

              {preview.data.existing_piece && (
                <p className="text-sm text-grey-600">
                  A {preview.data.existing_piece.publish_status} piece exists on this masthead; queuing replaces the draft.
                </p>
              )}

              <ol className="space-y-1.5 text-sm">
                {preview.data.members.map((m) => (
                  <li key={m.article_id} className="flex gap-2">
                    <span className="w-8 shrink-0 text-grey-400 font-mono">m{m.n}</span>
                    <span className="min-w-0">
                      <span className="font-medium text-grey-900">{m.name}</span>
                      {m.code && <span className="text-grey-500"> ({m.exchange}: {m.code})</span>}
                      {' · '}
                      <Link to={`/articles/${m.article_id}`} className="text-primary-600 hover:text-primary-700">
                        {m.title}
                      </Link>
                      {m.lodged_at && (
                        <span className="text-grey-400"> · {format(new Date(m.lodged_at), 'MMM d')}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => queue.mutate()}
                  disabled={queue.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {status === 'generated' ? <RefreshCw className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
                  {status === 'generated' ? 'Rewrite synthesis' : 'Queue synthesis'}
                </button>
                <span className="text-xs text-grey-500">Lands as a draft for review; nothing publishes automatically.</span>
              </div>
              {queue.isError && <p className="text-sm text-danger-600">{errorText(queue.error)}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
